import {CM_LOGGER_SCENE} from "@app/common/services/logging"
import {Component, DestroyRef, inject, input, OnInit, signal, viewChild, ChangeDetectionStrategy} from "@angular/core"
import {takeUntilDestroyed, toObservable, outputToObservable} from "@angular/core/rxjs-interop"
import {ConfiguratorComponent} from "@common/components/viewers/configurator/configurator/configurator.component"
import {MaterialGraph} from "@cm/material-nodes"
import {IsNonNull} from "@cm/utils"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {ignoreIfComponentDestroyed} from "@common/helpers/component-lifecycle/ignore-if-component-destroyed"
import {OrganizationsService} from "@common/services/organizations/organizations.service"
import {OrganizationsWithNodeEditorTemplateIdGQL} from "@material-editor/components/material-editor/material-editor.generated"
import {combineLatest} from "rxjs"
import {PreviewSceneService} from "@app/material-editor/services/preview-scene.service"
import {LocalStorageService} from "@app/common/services/local-storage/local-storage.service"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-preview-scene",
    templateUrl: "./preview-scene.component.html",
    styleUrls: ["./preview-scene.component.scss"],
    imports: [ConfiguratorComponent],
})
export class PreviewSceneComponent implements OnInit {
    private readonly logger = inject(CM_LOGGER_SCENE)
    readonly $configurator = viewChild.required<ConfiguratorComponent>("sceneViewer")

    readonly organizations = inject(OrganizationsService)
    private readonly localStorageService = inject(LocalStorageService)

    readonly $templateId = signal<string | undefined>(undefined)
    readonly $materialGraph = input.required<MaterialGraph>({alias: "materialGraph"})
    materialGraph$ = toObservable(this.$materialGraph)

    readonly organizationsWithNodeEditorTemplateId = inject(OrganizationsWithNodeEditorTemplateIdGQL)
    readonly previewSceneService = inject(PreviewSceneService)
    readonly destroyRef = inject(DestroyRef)

    async ngOnInit() {
        if (this.localStorageService.developmentMode) {
            const developmentTemplateId = "64b22bb1-9713-4c85-920e-82678ec8aa0c"
            this.logger.warn("Development mode is enabled, using development template")
            this.$templateId.set(developmentTemplateId)
        } else {
            const organizationTemplate = await this.getOrganizationTemplate()
            this.$templateId.set(organizationTemplate)
        }

        combineLatest([outputToObservable(this.$configurator().loadingCompleted), this.materialGraph$]).subscribe({
            next: ([_, materialGraph]) => {
                this.$configurator().setMaterialGraphToFirstInput(materialGraph)
            },
            error: ignoreIfComponentDestroyed,
        })

        this.previewSceneService.$refresh.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({next: () => this.refresh(), error: ignoreIfComponentDestroyed})
    }

    async getOrganizationTemplate() {
        const organizationIds = this.organizations.$own()?.map((org) => org.id) ?? []
        const {organizations} = await fetchThrowingErrors(this.organizationsWithNodeEditorTemplateId)({organizationIds})
        const templateIds = organizations.map((org) => org?.nodeEditorTemplate?.id).filter(IsNonNull)
        return templateIds[0]
    }

    refresh() {
        try {
            this.$configurator().localSceneManagerService.compileTemplate()
        } catch (error: unknown) {
            this.logger.warn("Error refreshing scene", error)
        }
    }
}
